import React, { useEffect, useState, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Button, Form, Alert, Badge, Card, Row, Col, Spinner } from "react-bootstrap"
import { FaArrowLeft, FaLock, FaLockOpen, FaTimes, FaTrash } from "react-icons/fa"
import CreatableSelect from "react-select/creatable"
import useFetch from "../../../_fetch"

interface PublisherUser {
    username: string
    userId: string
    addedAt: string
    active: boolean
}

interface Advertiser {
    username: string
    userId: string
    addedAt: string
}

interface Partnership {
    id: string
    name: string
    type: string
    mode: string
    value: number
    currency: string
    startDate: string
    expiryDate: string | null
    cookieDays: number
    createdAt: string
    domains: string[]
    locked?: boolean
    publisher_users: PublisherUser[]
    advertiser?: Advertiser
}

function cleanDomain(input: string): string | null {
    try {
        const url = input.startsWith("http") ? input : `https://${input}`
        const parsed = new URL(url)
        return parsed.hostname.toLowerCase().replace(/^www\d*\./i, "")
    } catch {
        return null
    }
}

function isValidDomain(domain: string) {
    return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain)
}

const PartnershipAgreementDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { doFetch } = useFetch()

    const [partnership, setPartnership] = useState<Partnership | null>(null)
    const [publisherUsernames, setPublisherUsernames] = useState<string[]>([])
    const [advertiserUsernames, setAdvertiserUsernames] = useState<string[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    // Settings form
    const [name, setName] = useState("")
    const [type, setType] = useState("")
    const [mode, setMode] = useState("")
    const [value, setValue] = useState("")
    const [currency, setCurrency] = useState("")
    const [startDate, setStartDate] = useState("")
    const [expiryDate, setExpiryDate] = useState("")
    const [cookieDays, setCookieDays] = useState("")
    const [locked, setLocked] = useState(false)
    const [domains, setDomains] = useState<string[]>([])
    const [domainError, setDomainError] = useState<string | null>(null)
    const [saveLoading, setSaveLoading] = useState(false)
    const [saveError, setSaveError] = useState<string | null>(null)
    const [saveSuccess, setSaveSuccess] = useState(false)

    // Add publisher
    const [addPublisherUsername, setAddPublisherUsername] = useState("")
    const [addPublisherError, setAddPublisherError] = useState<string | null>(null)
    const [addPublisherLoading, setAddPublisherLoading] = useState(false)

    // Set advertiser
    const [advertiserUsername, setAdvertiserUsername] = useState("")
    const [advertiserError, setAdvertiserError] = useState<string | null>(null)
    const [advertiserLoading, setAdvertiserLoading] = useState(false)

    const populate = (data: Partnership) => {
        setPartnership(data)
        setName(data.name || "")
        setType(data.type || "")
        setMode(data.mode || "")
        setValue(String(data.value ?? ""))
        setCurrency(data.currency || "")
        setStartDate(data.startDate || "")
        setExpiryDate(data.expiryDate || "")
        setCookieDays(String(data.cookieDays ?? ""))
        setLocked(!!data.locked)
        setDomains(Array.isArray(data.domains) ? data.domains : [])
    }

    const fetchPartnership = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await doFetch(`/user/admin/partnerships/${id}`)
            if (!res.ok) throw new Error(await res.text())
            populate(await res.json())
        } catch (err: any) {
            setError(err?.message || "Failed to load partnership")
        } finally {
            setLoading(false)
        }
    }, [doFetch, id])

    useEffect(() => {
        fetchPartnership()
        doFetch("/user/get-users")
            .then((r) => (r.ok ? r.json() : []))
            .then((users: any[]) => {
                const publishers = users
                    .filter((u) => u.kind === "publisher" && !u.isAdmin)
                    .map((u) => u.username)
                    .sort()
                const advertisers = users
                    .filter((u) => u.kind === "advertiser" && !u.isAdmin)
                    .map((u) => u.username)
                    .sort()
                setPublisherUsernames(publishers)
                setAdvertiserUsernames(advertisers)
            })
            .catch(() => {})
    }, [])

    const handleAddDomain = (option: any) => {
        const raw = option?.value?.trim()?.toLowerCase()
        if (!raw) return
        const cleaned = cleanDomain(raw)
        if (!cleaned || !isValidDomain(cleaned)) {
            setDomainError("Invalid domain")
            return
        }
        setDomainError(null)
        setDomains((prev) => (prev.includes(cleaned) ? prev : [...prev, cleaned]))
    }

    const handleRemoveDomain = (domain: string) => {
        setDomains((prev) => prev.filter((d) => d !== domain))
    }

    const handleSave = async () => {
        setSaveLoading(true)
        setSaveError(null)
        setSaveSuccess(false)
        try {
            const cookieDaysValue = Number(cookieDays)
            if (type !== "click" && (!Number.isInteger(cookieDaysValue) || cookieDaysValue < 0 || cookieDaysValue > 60)) {
                setSaveError("Cookie days must be an integer between 0 and 60")
                return
            }

            const res = await doFetch(
                `/user/admin/partnerships/${id}`,
                {
                    name,
                    type,
                    mode,
                    value: Number(value),
                    currency,
                    startDate,
                    expiryDate: expiryDate || null,
                    cookieDays: Number(cookieDays),
                    locked,
                    domains,
                },
                "PUT"
            )
            if (!res.ok) {
                const errBody = await res.json().catch(() => null)
                setSaveError(errBody?.error || "Failed to save")
                return
            }
            populate(await res.json())
            setSaveSuccess(true)
        } catch (err: any) {
            setSaveError(err?.message || "Failed to save")
        } finally {
            setSaveLoading(false)
        }
    }

    const handleAddPublisher = async () => {
        if (!addPublisherUsername) {
            setAddPublisherError("Select a user")
            return
        }
        setAddPublisherLoading(true)
        setAddPublisherError(null)
        try {
            const res = await doFetch(`/user/admin/partnerships/${id}/add-publisher`, { username: addPublisherUsername })
            if (!res.ok) {
                const errBody = await res.json().catch(() => null)
                setAddPublisherError(errBody?.error || "Failed")
                return
            }
            populate(await res.json())
            setAddPublisherUsername("")
        } catch (err: any) {
            setAddPublisherError(err?.message || "Failed")
        } finally {
            setAddPublisherLoading(false)
        }
    }

    const handleRemovePublisher = async (userId: string) => {
        try {
            const res = await doFetch(`/user/admin/partnerships/${id}/publisher/${userId}`, undefined, "DELETE")
            if (!res.ok) return
            populate(await res.json())
        } catch {}
    }

    const handleSetAdvertiser = async () => {
        if (!advertiserUsername) {
            setAdvertiserError("Select a user")
            return
        }
        setAdvertiserLoading(true)
        setAdvertiserError(null)
        try {
            const res = await doFetch(`/user/admin/partnerships/${id}/set-advertiser`, { username: advertiserUsername })
            if (!res.ok) {
                const errBody = await res.json().catch(() => null)
                setAdvertiserError(errBody?.error || "Failed")
                return
            }
            populate(await res.json())
            setAdvertiserUsername("")
        } catch (err: any) {
            setAdvertiserError(err?.message || "Failed")
        } finally {
            setAdvertiserLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="p-4 d-flex justify-content-center">
                <Spinner animation="border" />
            </div>
        )
    }

    if (error || !partnership) {
        return (
            <div className="p-4">
                <Alert variant="danger">{error || "Partnership not found"}</Alert>
                <Button variant="outline-secondary" size="sm" onClick={() => navigate("/partnership-agreements")}>
                    <FaArrowLeft className="me-1" /> Back
                </Button>
            </div>
        )
    }

    return (
        <div className="p-3" style={{ maxWidth: 860 }}>
            <div className="d-flex align-items-center mb-3 gap-3">
                <Button variant="outline-secondary" size="sm" onClick={() => navigate("/partnership-agreements")}>
                    <FaArrowLeft className="me-1" /> Partnerships
                </Button>
                <div>
                    <h5 className="mb-0">{partnership.name}</h5>
                    <code style={{ fontSize: "0.8rem", color: "#6c757d" }}>{partnership.id}</code>
                </div>
            </div>

            {/* ── Settings card ──────────────────────────────────────── */}
            <Card className="mb-3">
                <Card.Header style={{ fontWeight: 600 }}>Settings</Card.Header>
                <Card.Body>
                    {saveSuccess && (
                        <Alert variant="success" dismissible onClose={() => setSaveSuccess(false)}>
                            Changes saved.
                        </Alert>
                    )}
                    {saveError && (
                        <Alert variant="danger" dismissible onClose={() => setSaveError(null)}>
                            {saveError}
                        </Alert>
                    )}
                    <Row className="g-3">
                        <Col sm={12}>
                            <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>Name</Form.Label>
                            <Form.Control value={name} onChange={(e) => setName(e.target.value)} size="sm" />
                        </Col>
                        <Col sm={4}>
                            <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>Type</Form.Label>
                            <Form.Select value={type} onChange={(e) => setType(e.target.value)} size="sm">
                                <option value="sale">Sale</option>
                                <option value="click">Click</option>
                                <option value="lead">Lead</option>
                            </Form.Select>
                        </Col>
                        <Col sm={4}>
                            <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>Mode</Form.Label>
                            <Form.Select value={mode} onChange={(e) => setMode(e.target.value)} size="sm">
                                <option value="client">Client</option>
                                <option value="server">Server</option>
                            </Form.Select>
                        </Col>
                        <Col sm={4}>
                            <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>Currency</Form.Label>
                            <Form.Control
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                                maxLength={3}
                                size="sm"
                                placeholder="DKK"
                            />
                        </Col>
                        <Col sm={4}>
                            <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>
                                Value {type === "sale" ? "(%)" : `(${currency || "DKK"})`}
                            </Form.Label>
                            <Form.Control
                                type="number"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                size="sm"
                                min={0}
                                step={type === "sale" ? 0.01 : 1}
                            />
                        </Col>
                        <Col sm={4}>
                            <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>Start date</Form.Label>
                            <Form.Control
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                size="sm"
                            />
                        </Col>
                        <Col sm={4}>
                            <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>Expiry date</Form.Label>
                            <Form.Control
                                type="date"
                                value={expiryDate}
                                onChange={(e) => setExpiryDate(e.target.value)}
                                size="sm"
                            />
                        </Col>
                        {type !== "click" && (
                            <Col sm={4}>
                                <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>Cookie days</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={cookieDays}
                                    onChange={(e) => setCookieDays(e.target.value)}
                                    size="sm"
                                    min={0}
                                    max={60}
                                />
                            </Col>
                        )}

                        {/* Domains */}
                        <Col sm={12}>
                            <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>Partner domains</Form.Label>
                            <CreatableSelect
                                placeholder="Add domain (e.g. partner.com)"
                                isClearable
                                formatCreateLabel={(val) => `Add "${val}"`}
                                onChange={handleAddDomain}
                                onCreateOption={(val) => handleAddDomain({ value: val })}
                                value={null}
                            />
                            <div className="d-flex flex-wrap gap-2 mt-2">
                                {domains.map((d) => (
                                    <Badge key={d} bg="info" className="d-flex align-items-center px-2 py-1">
                                        {d}
                                        <FaTimes
                                            className="ms-2"
                                            style={{ cursor: "pointer" }}
                                            onClick={() => handleRemoveDomain(d)}
                                        />
                                    </Badge>
                                ))}
                            </div>
                            {domainError && <div className="text-danger small mt-2">{domainError}</div>}
                        </Col>

                        {/* Locked toggle — only visible when advertiser is set */}
                        {partnership.advertiser && (
                            <Col sm={12}>
                                <Form.Check
                                    type="switch"
                                    id="locked-switch"
                                    label={
                                        <span className="d-flex align-items-center gap-1" style={{ fontSize: "0.85rem" }}>
                                            {locked
                                                ? <FaLock style={{ color: "#6c757d" }} />
                                                : <FaLockOpen style={{ color: "#6c757d" }} />}
                                            {locked ? "Locked — publishers cannot edit" : "Unlocked — publishers can edit"}
                                        </span>
                                    }
                                    checked={locked}
                                    onChange={(e) => setLocked(e.target.checked)}
                                />
                            </Col>
                        )}
                    </Row>
                    <div className="mt-3 d-flex justify-content-end">
                        <Button variant="primary" size="sm" onClick={handleSave} disabled={saveLoading}>
                            {saveLoading ? <Spinner animation="border" size="sm" /> : "Save changes"}
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            {/* ── Publishers card ────────────────────────────────────── */}
            <Card className="mb-3">
                <Card.Header style={{ fontWeight: 600 }}>Publishers</Card.Header>
                <Card.Body>
                    {partnership.publisher_users.length === 0 ? (
                        <p className="text-muted mb-2" style={{ fontSize: "0.85rem" }}>No publishers added.</p>
                    ) : (
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            {partnership.publisher_users.map((p) => (
                                <div
                                    key={p.userId}
                                    className="d-flex align-items-center gap-1 border rounded px-2 py-1"
                                    style={{
                                        fontSize: "0.82rem",
                                        background: p.active ? "#e9ecef" : "#f8f9fa",
                                        color: p.active ? "#343a40" : "#adb5bd",
                                    }}
                                >
                                    <span style={{ fontSize: "0.78rem" }}>{p.username}</span>
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 text-danger"
                                        style={{ fontSize: "0.75rem", lineHeight: 1 }}
                                        title="Remove publisher"
                                        onClick={() => handleRemovePublisher(p.userId)}
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    {addPublisherError && (
                        <Alert variant="danger" dismissible onClose={() => setAddPublisherError(null)} className="py-1 mb-2">
                            {addPublisherError}
                        </Alert>
                    )}
                    <div className="d-flex gap-2 align-items-end">
                        <Form.Group style={{ flex: 1 }}>
                            <Form.Label style={{ fontSize: "0.82rem" }} className="mb-1">Add publisher</Form.Label>
                            <Form.Select
                                size="sm"
                                value={addPublisherUsername}
                                onChange={(e) => setAddPublisherUsername(e.target.value)}
                            >
                                <option value="">— select publisher —</option>
                                {publisherUsernames.map((u) => (
                                    <option key={u} value={u}>{u}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Button size="sm" variant="outline-primary" onClick={handleAddPublisher} disabled={addPublisherLoading}>
                            {addPublisherLoading ? <Spinner animation="border" size="sm" /> : "+ Add"}
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            {/* ── Advertiser card ────────────────────────────────────── */}
            <Card className="mb-3">
                <Card.Header style={{ fontWeight: 600 }}>Advertiser</Card.Header>
                <Card.Body>
                    {partnership.advertiser ? (
                        <div className="mb-3 d-flex align-items-center gap-2">
                            <span style={{ fontSize: "0.85rem" }}>Current:</span>
                            <span
                                style={{
                                    fontSize: "0.8rem",
                                    display: "inline-block",
                                    padding: "2px 8px",
                                    borderRadius: "4px",
                                    background: "#343a40",
                                    color: "#fff",
                                }}
                            >
                                {partnership.advertiser.username}
                            </span>
                        </div>
                    ) : (
                        <p className="text-muted mb-2" style={{ fontSize: "0.85rem" }}>No advertiser set.</p>
                    )}
                    {advertiserError && (
                        <Alert variant="danger" dismissible onClose={() => setAdvertiserError(null)} className="py-1 mb-2">
                            {advertiserError}
                        </Alert>
                    )}
                    <div className="d-flex gap-2 align-items-end">
                        <Form.Group style={{ flex: 1 }}>
                            <Form.Label style={{ fontSize: "0.82rem" }} className="mb-1">
                                {partnership.advertiser ? "Change advertiser" : "Set advertiser"}
                            </Form.Label>
                            <Form.Select
                                size="sm"
                                value={advertiserUsername}
                                onChange={(e) => setAdvertiserUsername(e.target.value)}
                            >
                                <option value="">— select advertiser —</option>
                                {advertiserUsernames.map((u) => (
                                    <option key={u} value={u}>{u}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Button
                            size="sm"
                            variant={partnership.advertiser ? "outline-secondary" : "outline-warning"}
                            onClick={handleSetAdvertiser}
                            disabled={advertiserLoading}
                        >
                            {advertiserLoading ? <Spinner animation="border" size="sm" /> : "Set"}
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </div>
    )
}

export default PartnershipAgreementDetail
